import { formatDate, t } from "@hakili/i18n";
import { GuardedButton } from "@hakili/ui";
import { ArrowLeft, CheckCircle2, Clock } from "lucide-react";
import type { ReactElement } from "react";

/**
 * `round T-2`, `M5` (`P2-CLW-005`, WF-CLI-27): **`/dossier/retrait`** , the bon de retrait with
 * its reference and its instructions, or the block and its reason while the settlement is not
 * `complet`. One read, `GET /api/v1/me/dossiers/{id}/pickup-voucher`: the voucher or the block
 * naming the awaited leg in the buyer's own words and nothing of the dealer's economics (I19,
 * I36). The PDF is greyed under R81 with its reason: the reference on the screen is the fact.
 */
export interface VoucherView {
  readonly voucher: {
    readonly reference: string;
    readonly issuedAt: string;
    readonly presentedAt: string | null;
  } | null;
  readonly blocked: { readonly missing: string; readonly reason: string } | null;
  readonly legs: {
    readonly dealerConfirmedAt: string | null;
    readonly hakiliReceivedAt: string | null;
    readonly status: string;
  };
  readonly vehicleLabel: string | null;
  readonly dealerName: string | null;
  /** `T-2b`: the split as the row froze it (whole percents); the standard's 90/10 when the door is older. */
  readonly shares: { readonly dealerPct: number; readonly hakiliPct: number };
  readonly payerKind: string;
}

const str = (value: unknown): string | null => (typeof value === "string" ? value : null);
const num = (value: unknown): number | null => (typeof value === "number" ? value : null);
const rec = (value: unknown): Record<string, unknown> | null =>
  typeof value === "object" && value !== null ? (value as Record<string, unknown>) : null;

export function voucherFrom(answer: unknown): VoucherView | null {
  const value = rec(answer);
  if (value === null) return null;
  const legs = rec(value["legs"]);
  const voucher = rec(value["voucher"]);
  const blocked = rec(value["blocked"]);
  const reference = str(voucher?.["reference"]);
  const issuedAt = str(voucher?.["issuedAt"]);
  const missing = str(blocked?.["missing"]);
  const reason = str(blocked?.["reason"]);
  return {
    voucher:
      reference === null || issuedAt === null
        ? null
        : { reference, issuedAt, presentedAt: str(voucher?.["presentedAt"]) },
    blocked: missing === null || reason === null ? null : { missing, reason },
    legs: {
      dealerConfirmedAt: str(legs?.["dealerConfirmedAt"]),
      hakiliReceivedAt: str(legs?.["hakiliReceivedAt"]),
      status: str(legs?.["status"]) ?? "attenteVirements",
    },
    vehicleLabel: str(value["vehicleLabel"]),
    dealerName: str(value["dealerName"]),
    shares: {
      dealerPct: num(rec(value["shares"])?.["dealerPct"]) ?? 90,
      hakiliPct: num(rec(value["shares"])?.["hakiliPct"]) ?? 10,
    },
    payerKind: str(value["payerKind"]) ?? "banque",
  };
}

const fill = (key: Parameters<typeof t>[0], values: Readonly<Record<string, string>>): string =>
  Object.entries(values).reduce((text, [hole, value]) => text.split(`{${hole}}`).join(value), t(key));

export function PickupVoucherScreen(props: {
  readonly view: VoucherView | null | undefined;
  readonly naviguer: (to: string) => void;
}): ReactElement {
  const { view } = props;
  return (
    <div className="px-5 pb-8 pt-4" data-testid="retrait-ecran">
      <button
        type="button"
        onClick={() => {
          props.naviguer("/suivi");
        }}
        className="mb-4 inline-flex items-center gap-1 text-sm text-indigo-600"
        data-testid="retrait-retour"
      >
        <ArrowLeft className="h-4 w-4" aria-hidden="true" />
        {t("client.suivi.titre")}
      </button>
      <h1 className="text-2xl font-semibold text-gray-900">{t("client.retrait.titre")}</h1>
      {view === undefined ? (
        <p className="mt-4 text-sm text-gray-500">{t("client.session.chargement")}</p>
      ) : view === null ? (
        <p className="mt-4 text-sm text-gray-500" data-testid="retrait-aucun">
          {t("client.retrait.aucun")}
        </p>
      ) : view.voucher === null ? (
        // WF-CLI-27's amber card: the reason names what is awaited, never the dealer's figures.
        <div
          className="mt-4 rounded-2xl border border-amber-200 bg-amber-50 p-4"
          data-testid="retrait-bloque"
        >
          <p className="flex items-center gap-2 text-base font-semibold text-amber-700">
            <Clock className="h-5 w-5" aria-hidden="true" />
            {t("client.retrait.bloqueTitre")}
          </p>
          <p className="mt-2 text-sm leading-relaxed text-amber-700" data-testid="retrait-bloque-motif">
            {view.blocked?.reason ?? ""}
          </p>
        </div>
      ) : (
        <>
          <div className="mt-4 rounded-2xl bg-indigo-600 p-4 text-white" data-testid="retrait-pret">
            <p className="flex items-center gap-2 text-base font-semibold">
              <CheckCircle2 className="h-5 w-5" aria-hidden="true" />
              {t("client.retrait.pretTitre")}
            </p>
            <p className="mt-1 text-sm text-indigo-100">{t("client.retrait.pretMots")}</p>
          </div>
          <div className="mt-4 rounded-2xl border border-gray-200 bg-white p-4" data-testid="retrait-bon">
            <p className="text-xs uppercase tracking-wide text-gray-500">{t("client.retrait.bon")}</p>
            <p className="mt-1 text-xl font-semibold text-gray-900" data-testid="retrait-bon-reference">
              {fill("client.retrait.bonReference", { reference: view.voucher.reference })}
            </p>
            <dl className="mt-3 space-y-1 text-sm text-gray-700">
              <div className="flex justify-between gap-3">
                <dt className="text-gray-500">{t("client.retrait.bonVehicule")}</dt>
                <dd className="text-right font-medium">{view.vehicleLabel ?? t("common.valeur.tiret")}</dd>
              </div>
              <div className="flex justify-between gap-3">
                <dt className="text-gray-500">{t("client.retrait.bonVendeur")}</dt>
                <dd className="text-right font-medium">{view.dealerName ?? t("common.valeur.tiret")}</dd>
              </div>
            </dl>
            <ul className="mt-3 space-y-1 text-sm text-gray-700" data-testid="retrait-bon-volets">
              <li>
                {view.legs.dealerConfirmedAt === null
                  ? t("client.suivi.argentAttente")
                  : fill("client.retrait.voletVendeur", {
                      payeur: t(
                        view.payerKind === "acheteur"
                          ? "client.retrait.payeurVous"
                          : "client.retrait.payeurBanque",
                      ),
                      part: String(view.shares.dealerPct),
                      date: formatDate(new Date(view.legs.dealerConfirmedAt)),
                    })}
              </li>
              <li>
                {view.legs.hakiliReceivedAt === null
                  ? t("client.suivi.argentAttente")
                  : fill("client.retrait.voletHakili", {
                      taux: String(view.shares.hakiliPct),
                      date: formatDate(new Date(view.legs.hakiliReceivedAt)),
                    })}
              </li>
            </ul>
            <p className="mt-3 text-sm leading-relaxed text-gray-600" data-testid="retrait-bon-atteste">
              {view.payerKind === "acheteur"
                ? t("client.retrait.bonAttesteComptant")
                : t("client.retrait.bonAtteste")}
            </p>
            {view.voucher.presentedAt === null ? null : (
              <p className="mt-2 text-sm font-medium text-emerald-700" data-testid="retrait-remis">
                {fill("client.retrait.remisLe", { date: formatDate(new Date(view.voucher.presentedAt)) })}
              </p>
            )}
            <div className="mt-3">
              <GuardedButton
                disabled
                reason={t("client.retrait.pdfMotif")}
                variant="secondary"
                testId="retrait-pdf"
              >
                {t("client.retrait.pdf")}
              </GuardedButton>
            </div>
          </div>
          <div
            className="mt-4 rounded-2xl border border-gray-200 bg-white p-4"
            data-testid="retrait-a-presenter"
          >
            <p className="text-sm font-semibold text-gray-900">{t("client.retrait.aPresenter")}</p>
            <ul className="mt-2 list-disc space-y-1 pl-5 text-sm text-gray-700">
              <li>{t("client.retrait.pieceIdentite")}</li>
              <li>{fill("client.retrait.bonDeRetrait", { reference: view.voucher.reference })}</li>
              <li>{t("client.retrait.attestation")}</li>
            </ul>
          </div>
          <div
            className="mt-4 rounded-2xl border border-gray-200 bg-white p-4"
            data-testid="retrait-a-verifier"
          >
            <p className="text-sm font-semibold text-gray-900">{t("client.retrait.aVerifier")}</p>
            <ul className="mt-2 list-disc space-y-1 pl-5 text-sm text-gray-700">
              <li>{t("client.retrait.verif1")}</li>
              <li>{t("client.retrait.verif2")}</li>
              <li>{t("client.retrait.verif3")}</li>
              <li>{t("client.retrait.verif4")}</li>
            </ul>
            <p className="mt-3 text-sm leading-relaxed text-gray-600">{t("client.retrait.ecart")}</p>
          </div>
        </>
      )}
    </div>
  );
}
